"use client";

import { useEffect, useRef } from "react";

/* The behaviour `role="dialog" aria-modal="true"` promises.

   An overlay that claims to be modal has to act like one: focus moves into it
   on open, Tab cycles inside it, Escape closes it, the page behind it does not
   scroll, and the control that opened it gets focus back on close. Every
   overlay in the app used to declare the attribute and do none of it, so a
   keyboard walked straight out of the dialog and into a page the screen reader
   had already been told was unavailable.

   `onClose` is an inline arrow at every call site, so it changes identity on
   every render. The effect reads the latest one through a ref and depends on
   `open` alone — depending on the handler would re-run focus-in on every
   keystroke and make the dialogs impossible to type in. */

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function useModalChrome(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      return;
    }

    const panel = ref.current;
    const opener = document.activeElement;
    const stops = () => (panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : []);

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || !panel) {
        return;
      }

      const focusable = stops();

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    stops().at(0)?.focus();

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";

      /* Only take focus back if nothing else has claimed it: the wallet closes
         itself to open the sign-in dialog, and restoring over that would undo
         the focus-in the next dialog just did. */
      const active = document.activeElement;
      const idle = active === null || active === document.body || panel?.contains(active) === true;

      if (idle && opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  return ref;
}
